import angular from 'angular';
import { EventEmitter } from 'modules/shared/EventEmitter';
import CardView from './messageTemplateCardView.html';
import './MessageTemplateCard.scss';
import { MessageTemplateFeatures } from '../MessageTemplateFeatures';
import { HTTP_URL_REGEX, MessageTemplateButtonTypes, CallToActionTypes, verifyActionsValueLength } from '../models/MessageTemplateButtons';
import { getAuthenticationTexts } from '../shared/LanguageMapping';
class MessageTemplateCardController {
    static $inject = ['$element', '$translate', '$scope', 'MessageTemplateUtil'];
    constructor($element, $translate, $scope, MessageTemplateUtil) {
        'ngInject';
        this.$element = $element;
        this.$translate = $translate;
        this.$scope = $scope;
        this.MessageTemplateUtil = MessageTemplateUtil;
        this.isButtonValid = true;
        this.isAuthentication = false;
        this.authenticationButtonText = '';
        this.authenticationMessageText = '';
        this.$scope = {
            ...this.$scope,
            editionEnabled: false,
            editionEnabledAuthentication: false,
            valueAuthentication: '',
            textAuthentication: '',
            previewMode: false,
            readMore: false,
            bold: false,
            italic: false,
            strikethrough: false,
            inputValue: '',
            value: '',
            selectedText: {
                finalText: '',
                initialText: '',
                textSelected: '',
            },
        };
    }
    async $onInit() {
        if (this.value) {
            this.$scope.inputValue = this.value;
            this.formatPreviewView({
                message: this.$scope.inputValue,
                enableReadMore: this.value.length > 240,
            });
        }
        await this.checkFeatures();
        // Initialize translations first for authentication
        if (this.isAuthentication) {
            await this.updateAuthenticationTranslations();
            // Check if $scope is available before setting up watchers
            if (this.$scope && typeof this.$scope.$watch === 'function') {
                // Main watcher
                this.$scope.$watch(() => this.language, (newLang, oldLang) => {
                    if (newLang && newLang !== oldLang) {
                        // Update immediately
                        this.updateAuthenticationTranslations();
                    }
                }, true); // Deep watch
                // Additional watcher observing parent object changes
                this.$scope.$watchCollection(() => {
                    // Return array with all properties we want to observe
                    return [this.language, this.isAuthentication];
                }, (newVals, oldVals) => {
                    if (newVals[0] !== oldVals[0]) {
                        this.updateAuthenticationTranslations();
                    }
                });
            }
        }
        if (!this.onlyPreview) {
            if (this.isMessageTemplateButtonsEnabled) {
                this.sandboxOptions = angular.copy(this.options);
                this.sandboxButtonType = this.buttonType;
            }
            else {
                this.options = [];
                this.buttonType = MessageTemplateButtonTypes.DEFAULT;
            }
        }
    }
    async $onChanges(changes) {
        // When translation language changes, update authentication template translations
        if (changes.language) {
            if (this.isAuthentication) {
                // Always update when language changes
                setTimeout(() => {
                    this.updateAuthenticationTranslations();
                }, 0);
            }
        }
    }
    async updateAuthenticationTranslations() {
        if (!this.isAuthentication)
            return;
        const selectedLanguage = this.language || 'en_US';
        // Get authentication texts using shared utility
        // For preview, fallback to English for unsupported languages
        // Note: When creating via Meta API, Meta provides correct translation for target language
        const { buttonText, messageText } = getAuthenticationTexts(selectedLanguage, true);
        // Update ALL possible ways, with $scope safety check
        if (this.$scope) {
            this.$scope.valueAuthentication = buttonText;
            this.$scope.textAuthentication = messageText;
        }
        this.authenticationButtonText = buttonText;
        this.authenticationMessageText = messageText;
        // Force view update safely
        try {
            if (this.$scope && typeof this.$scope.$apply === 'function' && !this.$scope.$$phase) {
                this.$scope.$apply();
            }
        }
        catch (e) {
            // Ignore if already in digest or scope destroyed
        }
    }
    // Public method to force translation updates
    forceUpdateTranslations() {
        if (this.isAuthentication) {
            this.updateAuthenticationTranslations();
        }
    }
    hasEmptyInputAuthentication() {
        if (!this.$scope.valueAuthentication) {
            return false;
        }
        return true;
    }
    async checkFeatures() {
        this.isMessageTemplateButtonsEnabled = await MessageTemplateFeatures.isMessageTemplateButtonsEnabled();
    }
    $onDestroy() {
        // Clean up (no polling interval to clear)
        setTimeout(() => {
            const textareaElement = this.getTextareaElement();
            textareaElement?.removeEventListener('select', this.onSelectText.bind(this));
            textareaElement?.removeEventListener('mouseup', () => setTimeout(this.clearSelectedText.bind(this), 50));
            textareaElement?.removeEventListener('keydown', () => setTimeout(this.clearSelectedText.bind(this), 50));
        }, 1000);
    }
    getTextareaElement() {
        const element = this.$element[0];
        return element.querySelector('textarea');
    }
    getPreviewElement() {
        const element = this.$element[0];
        return element.querySelector('.mt-card__preview .mt-card__preview__text');
    }
    addListenerOnTextarea() {
        const textareaElement = this.getTextareaElement();
        textareaElement.onselect = this.onSelectText.bind(this);
        textareaElement.onmouseup = () => setTimeout(this.clearSelectedText.bind(this), 50);
        textareaElement.onkeydown = () => setTimeout(this.clearSelectedText.bind(this), 50);
    }
    onSelectText(event) {
        const { selectionStart, selectionEnd, value } = event.target;
        const textSelected = document.getSelection().toString();
        const initialText = value.substring(0, selectionStart);
        const finalText = value.substring(selectionEnd, value.length);
        this.$scope.selectedText = { textSelected, initialText, finalText };
    }
    clearSelectedText() {
        const textSelected = document.getSelection().toString();
        if (!textSelected.length && this.$scope.selectedText.textSelected.length) {
            this.clearSelectedTextScope();
        }
    }
    clearSelectedTextScope() {
        this.$scope.selectedText = {
            finalText: '',
            initialText: '',
            textSelected: '',
        };
    }
    enableEditionMode() {
        this.$scope.editionEnabled = true;
        this.$scope.previewMode = false;
        this.$scope.value = this.$scope.inputValue;
    }
    enableEditionModeAuthentication() {
        this.$scope.editionEnabledAuthentication = true;
        this.$scope.previewMode = false;
        this.$scope.value = this.$scope.inputValue;
    }
    disableEditionMode() {
        this.$scope.editionEnabled = false;
    }
    disableEditionModeAuthentication() {
        this.$scope.editionEnabledAuthentication = false;
    }
    viewAllTextOnPreview() {
        this.formatPreviewView({
            message: this.value && this.onlyPreview ? this.value : undefined,
            enableReadMore: false,
        });
    }
    formatPreviewView({ message, enableReadMore }) {
        this.$scope.readMore = enableReadMore;
        this.$scope.previewMode = true;
        let value = message ? message : this.$scope.inputValue;
        value = enableReadMore ? value.substring(0, 239) : value;
        value = this.MessageTemplateUtil.formatMessageToPreviewView(value);
        this.getPreviewElement().innerHTML = enableReadMore ? `${value}...` : value;
    }
    addVariable() {
        const textareaElement = this.getTextareaElement();
        const variables = textareaElement.value.match(/{{\d{1,10}}}/gm);
        const lastVariable = variables
            ? variables.map(variable => parseInt(variable.replace(/[{}]/gm, '')))
                .sort((a, b) => a - b)[variables.length - 1]
            : 0;
        textareaElement.value += `{{${lastVariable + 1}}}`;
        textareaElement.focus();
    }
    addSymbolOnText(symbol) {
        const textareaElement = this.getTextareaElement();
        if (this.$scope.selectedText.textSelected.length) {
            const { finalText, initialText, textSelected } = this.$scope.selectedText;
            textareaElement.value = initialText + symbol + textSelected + symbol + finalText;
            this.clearSelectedTextScope();
            textareaElement.focus();
        }
        else {
            textareaElement.value += symbol + symbol;
            textareaElement.focus();
            const setInPosition = textareaElement.value.length - 1;
            textareaElement.setSelectionRange(setInPosition, setInPosition);
        }
    }
    applyBoldOnText() {
        this.addSymbolOnText('*');
    }
    applyItalicOnText() {
        this.addSymbolOnText('_');
    }
    applyStrikethroughOnText() {
        this.addSymbolOnText('~');
    }
    persistValue() {
        const haveValueInInput = this.$scope.inputValue.length > 0;
        haveValueInInput && this.formatPreviewView({ enableReadMore: this.$scope.inputValue.length > 240 });
    }
    closeEditionMode() {
        if (this.$scope.inputValue !== this.$scope.value) {
            this.$scope.inputValue = this.$scope.value;
        }
        this.persistValue();
        this.disableEditionMode();
        this.sandboxOptions = angular.copy(this.options);
        this.sandboxButtonType = this.buttonType;
    }
    closeEditionModeAuthentication() {
        if (this.$scope.inputValue !== this.$scope.value) {
            this.$scope.inputValue = this.$scope.value;
        }
        this.$scope.editionEnabledAuthentication = false;
    }
    hasButtons() {
        return (this.buttonType === MessageTemplateButtonTypes.QUICK_REPLY || this.buttonType === MessageTemplateButtonTypes.CALL_TO_ACTION);
    }
    verifyIfButtonsIsInvalid() {
        if (this.sandboxButtonType === MessageTemplateButtonTypes.DEFAULT) {
            return false;
        }
        const valueArr = this.sandboxOptions.map((item) => item.inputText) || [];
        const isDuplicate = valueArr.some((item, idx) => {
            return valueArr.indexOf(item) !== idx;
        });
        const hasInvalidButtonName = valueArr.find((item) => item?.length === 0 || item?.length > 20);
        if (this.sandboxButtonType === MessageTemplateButtonTypes.CALL_TO_ACTION) {
            const callToActionisValid = this.sandboxOptions.find(item => (item.url === '' && item.phone_number === '') ||
                (verifyActionsValueLength(item.url, true) || verifyActionsValueLength(item.phone_number, false)) ||
                (item.type === CallToActionTypes.URL &&
                    !new RegExp(HTTP_URL_REGEX).test(item.url)));
            if (callToActionisValid) {
                return true;
            }
        }
        return (isDuplicate || hasInvalidButtonName !== undefined);
    }
    save() {
        let currentValue = this.getTextareaElement().value;
        if (!currentValue.replace(/\s/g, '').length) {
            currentValue = '';
        }
        this.$scope.inputValue = currentValue;
        this.persistValue();
        this.disableEditionMode();
        const buttonsArray = [];
        if (this.isMessageTemplateButtonsEnabled) {
            this.options = angular.copy(this.sandboxOptions);
            this.buttonType = this.sandboxButtonType;
            if (this.buttonType !== MessageTemplateButtonTypes.DEFAULT) {
                this.options.map(button => {
                    if (button.type === CallToActionTypes.PHONE_NUMBER) {
                        buttonsArray.push({
                            type: button.type.toUpperCase(),
                            text: button.text,
                            phone_number: button.phone_number,
                        });
                    }
                    else if (button.type === CallToActionTypes.URL) {
                        buttonsArray.push({
                            type: button.type.toUpperCase(),
                            text: button.text,
                            url: button.url,
                        });
                    }
                    else {
                        buttonsArray.push({
                            type: this.buttonType.toUpperCase(),
                            text: button.text,
                        });
                    }
                });
            }
        }
        this.onConfirm(EventEmitter({ value: currentValue, buttonsArray }));
    }
    saveAuthentication() {
        this.disableEditionModeAuthentication();
        this.saveAuthenticationInternal();
    }
    saveAuthenticationInternal() {
        const buttonsArray = [];
        // Trim whitespace and ensure no invalid characters
        const buttonText = (this.$scope.valueAuthentication || '').trim();
        buttonsArray.push({
            type: 'OTP',
            otp_type: 'COPY_CODE',
            text: buttonText,
        });
        this.onConfirm(EventEmitter({ value: '', buttonsArray }));
    }
}
MessageTemplateCardController.$inject = ['$element', '$translate', '$scope', 'MessageTemplateUtil'];
export const MessageTemplateCardComponent = {
    template: CardView,
    controller: MessageTemplateCardController,
    controllerAs: '$ctrl',
    bindings: {
        onConfirm: '&',
        value: '@?',
        onlyPreview: '<?',
        buttonType: '=?',
        options: '=?',
        isAuthentication: '<?',
        thereTranslations: '&?',
        language: '<?',
    },
};
